import urlResolver from 'edrolo/utils/URLResolver';
import { get, post } from 'edrolo/domains/net/api';

// `classId` tells the exam endpoint which class to gate sections against.
// Without it the backend picks one of the caller's classes on the course
// itself, which for a teacher in several classes can be one whose tier
// hides every section — a 404 on a question set the topic page just showed
// them (UAT #86). Demo exams have no class context.
export const loadExam = (examId, isDemo, classId) => {
  const url = urlResolver.reverse(
    isDemo ? 'api:get:exam-detail:demo' : 'api:get:exam-detail:cached',
    { examId }
  );
  const query = !isDemo && classId ? `?school_class_id=${classId}` : '';
  return get(`${url}${query}`, { transformBody: (body) => body.toJS() });
};

export const loadExamSolution = (examId) => {
  const url = urlResolver.reverse('api:get:exam-detail-solution', {
    examId,
  });
  return get(url, { transformBody: (body) => body.toJS() });
};

export const loadSitting = (examId) => {
  const url = urlResolver.reverse('api:get:exam-sitting', { examId });
  return get(url, { transformBody: (body) => body.toJS() });
};

export const createSitting = (examId) => {
  // do actual request here..
  const url = urlResolver.reverse('api:post:exam-start', { examId });
  return post(url, {}, { transformBody: (body) => body.toJS() });
};

export const submitQuestionResponse = (
  questionId,
  sittingId,
  typeSpecificData = {},
  isDemo
) => {
  const url = urlResolver.reverse(
    isDemo
      ? 'api:post:exam-question-respond:demo'
      : 'api:post:exam-question-respond',
    {
      questionId,
    }
  );

  const {
    selectedAnswerId,
    multiSelectedAnswerIds,
    submittedText,
    fieldResponses,
  } = typeSpecificData;

  let data = buildQuestionResponsePayload(
    questionId,
    sittingId,
    selectedAnswerId,
    multiSelectedAnswerIds,
    submittedText,
    fieldResponses
  );

  return post(url, data, { transformBody: (body) => body.toJS() });
};

export const buildQuestionResponsePayload = (
  questionId,
  sittingId,
  selectedAnswerId,
  multiSelectedAnswerIds,
  submittedText,
  fieldResponses
) => {
  const data = {
    question: questionId,
    sitting: sittingId,
  };

  if (selectedAnswerId) {
    data.select_selected_answer = selectedAnswerId;
  }
  if (multiSelectedAnswerIds) {
    data.select_multi_selected_answers = multiSelectedAnswerIds;
  }

  // worked answers submit (falsy) empty strings
  if (typeof submittedText !== 'undefined') {
    data.text_submitted_text = submittedText;
  }

  if (fieldResponses) {
    data.field_responses = fieldResponses.map((fieldResponse) => ({
      field_id: fieldResponse.fieldId,
      submitted_value: fieldResponse.submittedValue,
      submitted_drawing_json: fieldResponse.submitted_drawing_json,
      submitted_drawing_svg: fieldResponse.submitted_drawing_svg,
      selected_option_id: fieldResponse.selectedOptionId,
    }));
  }

  return data;
};

export const submitQuestionGrokked = (questionId, sittingId, grokked) => {
  const url = urlResolver.reverse('api:post:exam-question-grok', {
    questionId,
  });

  const data = {
    question: questionId,
    sitting: sittingId,
    grokked,
  };

  return new post(url, data, { transformBody: (body) => body.toJS() });
};

export const submitQuestionMark = (questionId, sittingId, mark) => {
  const url = urlResolver.reverse('api:post:exam-question-mark', {
    questionId,
  });

  const data = {
    question: questionId,
    sitting_id: sittingId,
    text_marks_from_student: mark,
  };

  return new post(url, data, { transformBody: (body) => body.toJS() });
};

export const submitChecklistReflectionItem = (
  questionId,
  sittingId,
  checklistReflectionItem
) => {
  const url = urlResolver.reverse(
    'api:post:exam-question-checklist-reflection',
    {
      questionId,
    }
  );
  const { exemplar_response_checklist_item, checked } = checklistReflectionItem;

  const data = {
    // question: questionId,
    sitting: sittingId,
    exemplar_response_checklist_item,
    checked,
  };

  return post(url, data, { transformBody: (body) => body.toJS() });
};

export const loadClassQuestionResponses = (classId, examId, questionId) => {
  const url = urlResolver.reverse('api:get:class-exam-question-responses', {
    classId,
    examId,
    questionId,
  });
  return get(url, { transformBody: (body) => body.toJS() });
};

export async function loadClassExamSittings(classId, examId) {
  const url = urlResolver.reverse('api:class-exam-sitting-list', {
    classId,
    examId,
  });
  // note this api returns Immutable
  return get(url);
}

export const fetchClassQuestionSetAggregates = (examId, classId) => {
  const url = urlResolver.reverse(
    'api:get:class-exam-question-set-aggregates',
    {
      examId,
      classId,
    }
  );
  return get(url);
};
